import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Ticket, ChevronLeft, ChevronRight } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, formatDateTime } from "@/components/admin/ui";
import { TICKET_STATUS_META, TICKET_PRIORITY_META } from "./supportMeta";
import { TicketDetailDrawer } from "./TicketDetailDrawer";

const PAGE_SIZE = 20;
const selectClass = "rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white";

export const TicketsTab = ({ API, meta, canManage, onNewTicket, refreshKey }) => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", priority: "", category: "", search: "" });
  const [page, setPage] = useState(1);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const load = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.category) params.category = filters.category;
    if (filters.search) params.search = filters.search;
    axios.get(`${API}/org/support/tickets`, { params, withCredentials: true }).then(({ data }) => setResult(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, filters, page]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setPage(1); }, [filters]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="support-tickets-error">{error}</p>;

  const totalPages = result ? Math.max(1, Math.ceil(result.total / PAGE_SIZE)) : 1;

  return (
    <>
      <Panel
        title="Tickets"
        subtitle="Every Support Ticket, filtered to what you're authorized to see."
        testId="support-tickets-tab"
        action={canManage && (
          <button type="button" onClick={() => onNewTicket()} data-testid="support-tickets-new-btn" className="flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC]">
            <Plus size={14} /> New Ticket
          </button>
        )}
      >
        <div className="mb-4 flex flex-wrap gap-2" data-testid="support-tickets-filters">
          <input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Search subject, customer, ID…" data-testid="support-tickets-search" className={selectClass} />
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="support-tickets-filter-status" className={selectClass}>
            <option value="">All Statuses</option>
            {meta.statuses.map((s) => <option key={s} value={s}>{TICKET_STATUS_META[s].label}</option>)}
          </select>
          <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} data-testid="support-tickets-filter-priority" className={selectClass}>
            <option value="">All Priorities</option>
            {meta.priorities.map((p) => <option key={p} value={p}>{TICKET_PRIORITY_META[p].label}</option>)}
          </select>
          <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} data-testid="support-tickets-filter-category" className={selectClass}>
            <option value="">All Categories</option>
            {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {!result ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : result.tickets.length === 0 ? (
          <EmptyState icon={Ticket} title="No Tickets found." message="Create one, or clear your filters." testId="support-tickets-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="support-tickets-table">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400"><th className="pb-2">Ticket</th><th className="pb-2">Customer</th><th className="pb-2">Category</th><th className="pb-2">Priority</th><th className="pb-2">Status</th><th className="pb-2">Updated</th></tr></thead>
              <tbody>
                {result.tickets.map((t) => (
                  <tr key={t.ticket_id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50 dark:border-white/5 dark:hover:bg-white/5" onClick={() => setSelectedTicketId(t.ticket_id)} data-testid={`support-ticket-row-${t.ticket_id}`}>
                    <td className="py-2"><span className="font-bold text-[#0052FF]">{t.display_id}</span><p className="text-xs text-slate-400">{t.subject}</p></td>
                    <td className="py-2">{t.customer_name}</td>
                    <td className="py-2">{t.category}</td>
                    <td className="py-2"><span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${TICKET_PRIORITY_META[t.priority].cls}`}>{TICKET_PRIORITY_META[t.priority].label}</span></td>
                    <td className="py-2"><span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${TICKET_STATUS_META[t.status].cls}`}>{TICKET_STATUS_META[t.status].label}</span></td>
                    <td className="py-2 text-slate-500 dark:text-slate-400">{formatDateTime(t.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex items-center justify-between text-xs font-semibold text-slate-500 dark:text-slate-400">
              <span data-testid="support-tickets-total">{result.total} total</span>
              <div className="flex items-center gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="support-tickets-prev-page" className="rounded-lg border border-slate-200 p-1.5 disabled:opacity-30 dark:border-white/15"><ChevronLeft size={14} /></button>
                <span>Page {page} of {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="support-tickets-next-page" className="rounded-lg border border-slate-200 p-1.5 disabled:opacity-30 dark:border-white/15"><ChevronRight size={14} /></button>
              </div>
            </div>
          </div>
        )}
      </Panel>

      {selectedTicketId && (
        <TicketDetailDrawer API={API} ticketId={selectedTicketId} meta={meta} canManage={canManage} onClose={() => setSelectedTicketId(null)} onChanged={load} />
      )}
    </>
  );
};
